import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Button, 
  List, 
  Modal, 
  message, 
  Image, 
  Typography, 
  Divider,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col
} from 'antd';
import { 
  UploadOutlined, 
  FilePdfOutlined, 
  FileImageOutlined, 
  FileTextOutlined,
  EyeOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CameraOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadFile, getFiles, deleteFile, downloadFile, resolveFileURL } from '../services/localFileStorage';
import CameraCapture from './CameraCapture';

const { Text, Paragraph } = Typography;

const FileAttachments = ({ 
  parayanaId, 
  attachments = [], 
  onAttachmentsChange
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [cameraVisible, setCameraVisible] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);
  const { getUserId } = useAuth();

  // Load files from local storage
  useEffect(() => {
    const loadLocalFiles = async () => {
      if (!getUserId() || !parayanaId || parayanaId === 'new') return;
      
      try {
        const files = await getFiles(getUserId(), parayanaId);
        setLocalAttachments(files);
        
        // Convert to file list format
        const convertedFiles = files.map((file, index) => ({
          uid: file.id,
          name: file.name,
          status: 'done',
          url: file.url,
          type: file.type,
          size: file.size,
          uploadedAt: file.uploadedAt
        }));
        setFileList(convertedFiles);
        
        // Update parent component
        onAttachmentsChange(files);
      } catch (error) {
        console.error('Failed to load local files:', error);
      }
    };

    loadLocalFiles();
  }, [getUserId, parayanaId, onAttachmentsChange]);

  // Also handle external attachments (for compatibility)
  useEffect(() => {
    if (attachments && attachments.length > 0) {
      const convertedFiles = attachments.map((attachment, index) => ({
        uid: attachment.id || index,
        name: attachment.name,
        status: 'done',
        url: attachment.url,
        type: attachment.type,
        size: attachment.size,
        uploadedAt: attachment.uploadedAt
      }));
      setFileList(prev => {
        // Merge with local files, avoiding duplicates
        const localIds = prev.map(f => f.uid);
        const newFiles = convertedFiles.filter(f => !localIds.includes(f.uid));
        return [...prev, ...newFiles];
      });
    }
  }, [attachments]);

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4757' }} />;
    if (type?.includes('image')) return <FileImageOutlined style={{ color: '#2ed573' }} />;
    return <FileTextOutlined style={{ color: '#3742fa' }} />;
  };

  const getFileTypeTag = (type) => {
    if (type?.includes('pdf')) return <Tag color="red">PDF</Tag>;
    if (type?.includes('image')) return <Tag color="green">Image</Tag>;
    if (type?.includes('text')) return <Tag color="blue">Text</Tag>;
    return <Tag color="default">File</Tag>;
  };

  const handleFileUpload = async (file) => {
    const userId = getUserId();
    if (!userId || !parayanaId || parayanaId === 'new') {
      message.error('Please save the parayana first before adding files');
      return;
    }

    try {
      setUploading(true);
      
      // Upload to local storage
      const uploadedFile = await uploadFile(file, userId, parayanaId);
      
      // Update local state
      const newFileItem = {
        uid: uploadedFile.id,
        name: uploadedFile.name,
        status: 'done',
        url: uploadedFile.url,
        type: uploadedFile.type,
        size: uploadedFile.size,
        uploadedAt: uploadedFile.uploadedAt
      };
      
      setFileList(prev => [...prev, newFileItem]);
      setLocalAttachments(prev => [...prev, uploadedFile]);
      
      // Update parent component
      const updatedAttachments = [...localAttachments, uploadedFile];
      onAttachmentsChange(updatedAttachments);
      
      message.success(`Successfully uploaded ${file.name}! 📎`);
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async ({ fileList: newFileList }) => {
    // Process new files
    const newFiles = newFileList.filter(file => 
      file.status !== 'done' && file.originFileObj
    );

    for (const file of newFiles) {
      await handleFileUpload(file.originFileObj);
    }
  };

  const handleCameraCapture = async (file) => {
    await handleFileUpload(file);
    setCameraVisible(false);
  };

  const handleDelete = async (file) => {
    try {
      // Delete from local storage
      await deleteFile(file.uid);
      
      // Update local state
      setFileList(prev => prev.filter(f => f.uid !== file.uid));
      const updatedAttachments = localAttachments.filter(att => att.id !== file.uid);
      setLocalAttachments(updatedAttachments);
      
      // Update parent component
      onAttachmentsChange(updatedAttachments);
      
      message.success('File deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      message.error('Failed to delete file');
    }
  };

  const handleDownload = async (file) => {
    try {
      await downloadFile(file.uid, file.name);
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download file');
    }
  };

  const handlePreview = async (file) => {
    if (!file.url) return;
    
    setPreviewTitle(file.name);
    
    try {
      // Resolve the file URL for local files
      const resolvedUrl = await resolveFileURL(file.url);
      
      if (file.type?.includes('image')) {
        setPreviewContent(
          <div style={{ textAlign: 'center' }}>
            <Image 
              src={resolvedUrl} 
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
              preview={false}
            />
          </div>
        );
      } else if (file.type?.includes('pdf')) {
        setPreviewContent(
          <div>
            <iframe 
              src={resolvedUrl} 
              style={{ width: '100%', height: '60vh', border: 'none', borderRadius: '8px' }}
              title={file.name}
            />
          </div>
        );
      } else {
        // For text files, try to fetch and display content
        try {
          const response = await fetch(resolvedUrl);
          const text = await response.text();
          setPreviewContent(
            <div>
              <Paragraph 
                style={{ 
                  maxHeight: '60vh', 
                  overflow: 'auto', 
                  padding: '16px',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  whiteSpace: 'pre-wrap',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
              >
                {text}
              </Paragraph>
            </div>
          );
        } catch (error) {
          setPreviewContent(
            <Text type="secondary">Cannot preview this file type. Click download to view.</Text>
          );
        }
      }
    } catch (error) {
      setPreviewContent(
        <Text type="secondary">Failed to load file preview.</Text>
      );
    }
    
    setPreviewVisible(true);
  };

  const uploadProps = {
    multiple: true,
    fileList: [], // Don't show Ant Design's upload list
    onChange: handleUpload,
    beforeUpload: (file) => {
      // Validate file type and size
      const isValidType = file.type?.includes('pdf') || 
                         file.type?.includes('image') || 
                         file.type?.includes('text') ||
                         file.name?.endsWith('.txt');
      
      if (!isValidType) {
        message.error('Only PDF, image, and text files are allowed');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10; // 10MB limit
      if (!isLt10M) {
        message.error('File must be smaller than 10MB');
        return false;
      }
      
      return false; // Prevent auto upload, we handle it manually
    },
    showUploadList: false, // We'll use custom list
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ color: '#FF8C00' }}>📎 Study Materials</Text>
        {fileList.length > 0 ? (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Your uploaded study materials for this parayana
          </div>
        ) : (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            No study materials uploaded yet
          </div>
        )}
      </div>
      
      {/* Upload buttons - HIDDEN as requested
      <Row gutter={12} style={{ marginBottom: '16px' }}>
        <Col>
          <Upload {...uploadProps}>
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              style={{ 
                borderColor: '#FFB347',
                color: '#FF8C00'
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </Upload>
        </Col>
        <Col>
          <Button
            icon={<CameraOutlined />}
            onClick={() => setCameraVisible(true)}
            style={{ 
              borderColor: '#FFB347',
              color: '#FF8C00'
            }}
          >
            Take Photo
          </Button>
        </Col>
      </Row>
      */}

      {fileList.length > 0 && (
        <div>
          <Divider style={{ margin: '16px 0' }} />
          <List
            dataSource={fileList}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(file)}
                    title="Preview"
                  />,
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(file)}
                    title="Download"
                  />,
                  <Popconfirm
                    title="Delete this file?"
                    onConfirm={() => handleDelete(file)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      title="Delete"
                    />
                  </Popconfirm>
                ]}
                style={{ padding: '8px 0' }}
              >
                <List.Item.Meta
                  avatar={getFileIcon(file.type)}
                  title={
                    <Space>
                      <Text style={{ fontSize: '14px' }}>{file.name}</Text>
                      {getFileTypeTag(file.type)}
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''} 
                      {file.uploadedAt && ` • ${new Date(file.uploadedAt).toLocaleDateString()}`}
                      <span style={{ marginLeft: '8px', color: '#52c41a' }}>• Local Storage 💾</span>
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Camera Modal - HIDDEN since upload functionality is disabled
      <CameraCapture
        visible={cameraVisible}
        onCancel={() => setCameraVisible(false)}
        onCapture={handleCameraCapture}
      />
      */}

      {/* File Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        style={{ top: 20 }}
        destroyOnClose
      >
        {previewContent}
      </Modal>
    </div>
  );
};

export default FileAttachments; 